<template>
  <ElDialog
    width="100%"
    top="6vh"
    :visible="dialogIsVisible"
    class="guest-export-dialog"
    @close="onDialogClose"
  >
    <p slot="title">Export your guests</p>

    <h2 class="text-xl mb-10">Export preview</h2>

    <div class="export-preview-container">
      <div v-html="exportPreview.html"></div>
      <div
        v-if="exportPreview.moreRowsCount"
        class="more-guests"
      >
        {{ '+ ' + exportPreview.moreRowsCount + ' more guests' }}
      </div>
    </div>
    <div
      slot="footer"
      class="dialog-footer flex justify-end laptop:flex-row-reverse"
    >
      <ElButton
        type="text"
        class="mr-20 laptop:mr-0 laptop:ml-20"
        @click="onDialogClose"
      >
        Cancel
      </ElButton>
      <ElButton
        type="primary"
        :disabled="guestsIsEmpty"
        class="action-button"
        @click="onGuestExportPress"
      >
        {{ exportButtonLabel }}
      </ElButton>
    </div>

    <ExportForm :event-title="event && event.title ? event.title : ''" />
  </ElDialog>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import XLSX from 'xlsx';
import { guestStatus, browserResolvedLocaleOptions } from '@/utils/utilityFunctions';
import { getGuestIdFormatValue, getQRCodeURL, getBarcodeURL } from '@/utils/barcodeGeneration';
import ExportForm from './GuestExportForm';

const defaultPreviewGuestCount = 5;

export default {
  props: {
    event: {
      type: Object,
      default: null,
    },
  },
  components: {
    ExportForm,
  },
  data: () => ({
    exportPreview: {
      html: '',
      moreRowsCount: 0,
      defaultPreviewGuestCount,
      previewGuestCount: defaultPreviewGuestCount,
    },
    browserResolvedLocaleOptions,
  }),
  watch: {
    'exportDetails.guestSelection': {
      immediate: true,
      handler() {
        if (this.dialogIsVisible) this.generateExportPreview();
      },
    },
    'exportDetails.exportQRCodeData': {
      immediate: true,
      handler() {
        if (this.dialogIsVisible) this.generateExportPreview();
      },
    },
    dialogIsVisible: {
      immediate: true,
      handler(newValue) {
        if (newValue) this.generateExportPreview();
      },
    },
    guests: {
      immediate: true,
      handler() {
        if (this.dialogIsVisible) this.generateExportPreview();
      },
    },
  },
  computed: {
    ...mapState('user/teamMembers', {
      allTeamMembers: 'all',
    }),
    ...mapState('guest', {
      guests: 'guests',
      guestsCounts: 'guestsCounts',
    }),
    ...mapState('guest/export', {
      dialogIsVisible: 'dialogIsVisible',
      exportDetails: 'exportDetails',
    }),
    ...mapGetters('user/teamMembers', {
      teamMembersAreLoaded: 'teamMembersAreLoaded',
    }),
    ...mapGetters('guest', {
      guestsIsEmpty: 'guestsIsEmpty',
    }),
    ...mapGetters('guest/filter', {
      filteredGuests: 'filteredGuests',
    }),
    exportGuests() {
      let guests = [];
      let guestsCounts = {
        mainGuest: 0,
        extraGuest: 0,
      };

      if (!this.dialogIsVisible) return { guests, guestsCounts };

      if (this.exportDetails.guestSelection === 'all') {
        if (this.exportPreview.previewGuestCount) guests = this.guests.slice(0, this.exportPreview.previewGuestCount);
        else ({ guests } = this);

        ({ guestsCounts } = this);
      } else {
        if (this.exportPreview.previewGuestCount) {
          guests = this.filteredGuests.guests.slice(0, this.exportPreview.previewGuestCount);
        } else ({ guests } = this.filteredGuests);

        ({ guestsCounts } = this.filteredGuests);
      }

      guests = guests.map((guest) => {
        const guestExportDetails = { // Guest data that is always exported
          firstName: guest.firstName,
          lastName: guest.lastName,
          email: guest.email || '',
          status: guestStatus[guest.status]?.name || guestStatus.default.name,
          vip: guest.vip,
          phoneNumber: guest.phoneNumber || '',
          jobTitle: guest.jobTitle || '',
          organization: guest.organization || '',
          note: guest.note || '',
          totalExtraGuests: guest.totalExtraGuests,
          checkedInExtraGuests: guest.checkedInExtraGuests || '',
          checkInTime: guest.checkInTime || '',
          creationTime: guest.creationTime || '',
        };
        if (this.event?.isSupportingCheckOut) { // Exporting checkOutTime if the event supports it
          guestExportDetails.checkOutTime = guest.checkOutTime || '';
        }
        if (guest.createdBy && this.allTeamMembers[guest.createdBy]?.name) {
          guestExportDetails.createdBy = this.allTeamMembers[guest.createdBy]?.name || '';

          if (this.allTeamMembers[guest.createdBy]?.email) {
            guestExportDetails.createdBy += ` - ${this.allTeamMembers[guest.createdBy]?.email || ''}`;
          }
        }

        if (this.event?.isSupportingQRCode) {
          // Custom barcode value is enabled
          if (this.event?.guestIdFieldForCheckIn === 'barcodeValue') {
            if (guest?.barcodeValue) {
              if (this.event?.barcodeType !== 'Code39') { // QRCode type barcode
                guestExportDetails.barcodeLink = getQRCodeURL({ value: guest.barcodeValue });
              } else { // Code39 type barcode
                guestExportDetails.barcodeLink = getBarcodeURL(guest.barcodeValue);
              }
              if (this.exportDetails.exportQRCodeData) guestExportDetails.barcodeData = guest.barcodeValue;
            }
          } else { // Default Eventor QRCode format (Guest ID)
            guestExportDetails.barcodeLink = getQRCodeURL({ value: guest.id, defaultGuestIdFormat: true });
            if (this.exportDetails.exportQRCodeData) guestExportDetails.barcodeData = getGuestIdFormatValue(guest.id);
          }
        }

        return guestExportDetails;
      });

      return {
        guests,
        guestsCounts,
      };
    },
    exportButtonLabel() {
      if (this.exportGuests.guestsCounts.mainGuest) return `Export ${this.exportGuests.guestsCounts.mainGuest} guests`;
      return 'Export';
    },
  },
  methods: {
    ...mapActions('guest/export', {
      updateDialogIsVisible: 'updateDialogIsVisible',
    }),
    onDialogClose() {
      this.updateDialogIsVisible(false);
    },
    onGuestExportPress() {
      this.exportPreview.previewGuestCount = 0;

      const fileWriteOpts = { bookType: this.exportDetails.fileFormat };
      const workBook = XLSX.utils.book_new(); // Create a new blank workbook
      let workSheet = XLSX.utils.json_to_sheet(this.exportGuests.guests, {
        header: this.exportDetails.fileSheetColumnOrder,
        dateNF: browserResolvedLocaleOptions.hour12 ? 'mm/dd/yyyy h:mm:ss AM/PM' : 'dd/mm/yyyy H:mm:ss',
      });
      let fileName = this.exportDetails.fileName === '' ? this.event.title : this.exportDetails.fileName;
      fileName += `.${this.exportDetails.fileFormat}`;

      workSheet = this.renameColumnHeaders(workSheet);
      workSheet = this.defineColumnWidth(workSheet);

      // Add the worksheet to the workbook
      XLSX.utils.book_append_sheet(workBook, workSheet, this.exportDetails.fileSheetName);

      /* generate file and force a download */
      XLSX.writeFile(workBook, fileName, fileWriteOpts);
    },
    renameColumnHeaders(workSheet) {
      const updatedWorkSheet = workSheet;

      // Update headers
      const range = XLSX.utils.decode_range(workSheet['!ref']);
      for (let colIndex = 0; colIndex <= range.e.c; colIndex += 1) {
        const colAddress = `${XLSX.utils.encode_col(colIndex)}1`;

        // eslint-disable-next-line no-continue
        if (!workSheet[colAddress]) continue;

        const value = workSheet[colAddress].v;
        let newValue = '';

        if (value === 'firstName') newValue = 'First name'; // TODO: Create utility function
        else if (value === 'lastName') newValue = 'Last name';
        else if (value === 'email') newValue = 'Email address';
        else if (value === 'status') newValue = 'Status';
        else if (value === 'vip') newValue = 'VIP';
        else if (value === 'phoneNumber') newValue = 'Phone number';
        else if (value === 'jobTitle') newValue = 'Job title';
        else if (value === 'organization') newValue = 'Organization';
        else if (value === 'note') newValue = 'Note';
        else if (value === 'totalExtraGuests') newValue = 'Total extra\'s';
        else if (value === 'checkedInExtraGuests') newValue = 'Checked-in extra\'s';
        else if (value === 'checkInTime') newValue = 'Checked-in at';
        else if (value === 'checkOutTime') newValue = 'Checked-out at';
        else if (value === 'creationTime') newValue = 'Created on';
        else if (value === 'createdBy') newValue = 'Created by';
        else if (value === 'barcodeLink' && this.event?.isSupportingQRCode) {
          if (this.event?.guestIdFieldForCheckIn === 'barcodeValue') {
            newValue = `Barcode (${this.event?.barcodeType === 'Code39' ? 'Code39' : 'QR Code'}) link`;
          } else newValue = 'QR Code';
        } else if (value === 'barcodeData' && this.event?.isSupportingQRCode && this.exportDetails.exportQRCodeData) {
          if (this.event?.guestIdFieldForCheckIn === 'barcodeValue') {
            newValue = `Barcode (${this.event?.barcodeType === 'Code39' ? 'Code39' : 'QR Code'}) data`;
          } else newValue = 'QR Code data';
        }

        updatedWorkSheet[colAddress].v = newValue;
      }

      return updatedWorkSheet;
    },
    defineColumnWidth(workSheet) {
      if (this.exportDetails.fileType !== 'Excel') return workSheet;

      const updatedWorkSheet = workSheet;
      const range = XLSX.utils.decode_range(updatedWorkSheet['!ref']);
      const wscols = [];
      let rowNum;
      let colNum;
      let maxCharsForCol;

      for (colNum = 0; colNum <= range.e.c; colNum += 1) {
        const colHeaderValue = updatedWorkSheet[XLSX.utils.encode_cell({ r: 0, c: colNum })].v;
        maxCharsForCol = 5; // Min of 5

        if (colHeaderValue === 'Checked-in at' || colHeaderValue === 'Checked-out at') {
          maxCharsForCol = 22;
          wscols.push({ wch: maxCharsForCol });
          // eslint-disable-next-line no-continue
          continue;
        }

        for (rowNum = 0; rowNum <= range.e.r; rowNum += 1) {
          const cell = updatedWorkSheet[XLSX.utils.encode_cell({ r: rowNum, c: colNum })];

          if (cell?.v && cell.v.length > maxCharsForCol) maxCharsForCol = cell.v.length + 4;
        }

        wscols.push({ wch: maxCharsForCol });
      }

      updatedWorkSheet['!cols'] = wscols;

      return updatedWorkSheet;
    },
    generateExportPreview() {
      this.exportPreview.previewGuestCount = defaultPreviewGuestCount;
      let workSheet = XLSX.utils.json_to_sheet(this.exportGuests.guests,
        { header: this.exportDetails.fileSheetColumnOrder });

      // TODO: Apply reordering of columns (first name first, last name first)
      workSheet = this.renameColumnHeaders(workSheet);

      this.exportPreview.html = XLSX.utils.sheet_to_html(workSheet);
      if (this.exportGuests.guestsCounts.mainGuest < this.exportPreview.previewGuestCount) {
        this.exportPreview.moreRowsCount = 0;
      } else {
        this.exportPreview.moreRowsCount = this.exportGuests.guestsCounts.mainGuest
          - this.exportPreview.previewGuestCount;
      }
    },
  },
};
</script>

<style scoped>
.export-preview-container {
  @apply p-20 mb-20 overflow-auto;

  background-color: #f3f3f3;

  >>> table {
    border-collapse: separate;
    border-spacing: 15px 5px;
    table-layout: fixed;
    margin: -15px -10px;

    tr {
      margin-bottom: 0.5rem;
    }

    tr:first-child td { /* First row */
      padding: 15px 0 5px 0;
      font-size: 0.95rem;
      font-weight: bold;
    }

    td {
      white-space: nowrap;
    }
  }

  .more-guests {
    margin-top: 1.5rem;
    font-style: italic;
  }
}

.guest-export-dialog >>> .el-dialog {
  max-width: 800px;
}

.action-button {
  min-width: 190px;
  max-width: 210px;
}
</style>
